import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Quotes, ArrowRight, Spinner, CheckCircle, Tag } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackFormSubmit } from "@/hooks/useTrafficTracker";

const SERVICES = [
  "Appliance Repair and Installation",
  "Courier Services",
  "Property and Home Services",
  "Other",
];

const initial = { name: "", email: "", city: "", rating: 5, quote: "", service_used: "" };

export default function ReviewsPage() {
  const [form, setForm] = useState(initial);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/reviews"); setReviews(data); }
      catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.quote.trim() || form.quote.trim().length < 8) e.quote = "Please share at least a sentence";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email or leave empty";
    setErrors(e); return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        name: form.name.trim(),
        email: form.email.trim() || null,
        city: form.city || null,
        rating: Number(form.rating),
        quote: form.quote.trim(),
        service_used: form.service_used || null,
      });
      trackFormSubmit("review");
      toast.success("Thanks! Your review will show up after David approves it.");
      setSubmitted(true); setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit review");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="reviews-page">
      <section className="ji-container pt-16 md:pt-24 pb-10">
        <div className="ji-overline mb-5">Customer Reviews</div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          Word from<br /><span className="text-[var(--ji-accent)] italic">the neighborhood.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          Real reviews from real Joseph Industries customers. Have a story of your own? Leave a review below — we'll post it after a quick review.
        </p>
      </section>

      <section className="ji-container pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Reviews list */}
        <div className="lg:col-span-7" data-testid="reviews-list">
          <div className="ji-overline mb-6">Recent reviews</div>
          {loading ? (
            <div className="py-16 text-center text-[var(--ji-muted)]"><Spinner size={22} className="animate-spin mx-auto" /> Loading…</div>
          ) : reviews.length === 0 ? (
            <div className="ji-card p-10 text-center text-[var(--ji-muted)]" style={{ borderRadius: 4 }}>
              No reviews yet — be the first to share your experience.
            </div>
          ) : (
            <div className="space-y-5">
              {reviews.map((r, i) => (
                <div key={r.id} data-testid={`review-${i}`} className="ji-card p-6" style={{ borderRadius: 4 }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, k) => (
                        <Star key={k} size={16} weight={k < r.rating ? "fill" : "regular"} className={k < r.rating ? "text-[var(--ji-accent)]" : "text-[var(--ji-border)]"} />
                      ))}
                    </div>
                    {r.service_used && <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)]">{r.service_used}</span>}
                  </div>
                  <Quotes size={20} weight="fill" className="text-[var(--ji-accent)] mt-4" />
                  <p className="mt-2 text-base leading-relaxed">"{r.quote}"</p>
                  <div className="mt-4 pt-4 border-t border-[var(--ji-border)] text-sm">
                    <span className="font-semibold">{r.name}</span>
                    {r.city && <span className="text-[var(--ji-muted)]"> · {r.city}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit form */}
        <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <div className="ji-card p-7 md:p-8" style={{ borderRadius: 4 }}>
            {submitted ? (
              <div className="py-6 text-center" data-testid="review-success">
                <CheckCircle size={40} weight="duotone" className="text-[var(--ji-accent)] mx-auto" />
                <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-3 text-2xl font-bold tracking-tight">Thanks!</h3>
                <p className="mt-2 text-sm text-[var(--ji-muted)]">Your review is being reviewed by David and will show up shortly.</p>
                <button data-testid="review-another" onClick={() => setSubmitted(false)} className="ji-btn-outline mt-5 inline-flex items-center gap-2 px-5 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
                  Leave another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="review-form" className="space-y-4">
                <div className="ji-overline inline-flex items-center gap-2"><Tag size={12} weight="fill" /> Leave a review</div>
                <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold tracking-tight">Share your experience.</h3>

                <div>
                  <Label className="text-sm font-medium">Rating</Label>
                  <div className="mt-2 flex gap-1" data-testid="review-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => update("rating", n)} data-testid={`review-star-${n}`} aria-label={`${n} star${n > 1 ? "s" : ""}`}>
                        <Star size={28} weight={n <= form.rating ? "fill" : "regular"} className={n <= form.rating ? "text-[var(--ji-accent)]" : "text-[var(--ji-border)] hover:text-[var(--ji-accent)]/60"} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="r-name" className="text-sm font-medium">Your name *</Label>
                  <Input id="r-name" data-testid="review-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="h-11 mt-1" placeholder="Jane Doe" />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="r-city" className="text-sm font-medium">City (optional)</Label>
                  <Input id="r-city" data-testid="review-city" value={form.city} onChange={(e) => update("city", e.target.value)} className="h-11 mt-1" placeholder="Metairie, LA" />
                </div>
                <div>
                  <Label htmlFor="r-email" className="text-sm font-medium">Email (optional, not shown)</Label>
                  <Input id="r-email" type="email" data-testid="review-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-11 mt-1" placeholder="you@email.com" />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label className="text-sm font-medium">Service you used</Label>
                  <Select value={form.service_used} onValueChange={(v) => update("service_used", v)}>
                    <SelectTrigger data-testid="review-service" className="h-11 mt-1"><SelectValue placeholder="Select service" /></SelectTrigger>
                    <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="r-quote" className="text-sm font-medium">Your review *</Label>
                  <Textarea id="r-quote" data-testid="review-quote" value={form.quote} onChange={(e) => update("quote", e.target.value)} className="mt-1 min-h-[120px]" placeholder="Tell folks what we did and how it went…" />
                  {errors.quote && <p className="text-xs text-red-600 mt-1">{errors.quote}</p>}
                </div>
                <button type="submit" data-testid="review-submit" disabled={submitting} className="ji-btn-primary w-full inline-flex items-center justify-center gap-2 px-6 h-12 text-sm font-semibold disabled:opacity-60" style={{ borderRadius: 4 }}>
                  {submitting ? <><Spinner size={16} className="animate-spin" /> Submitting…</> : <>Submit review <ArrowRight size={14} weight="bold" /></>}
                </button>
                <p className="text-[10px] text-[var(--ji-muted)]">Reviews are held for moderation and posted after approval.</p>
              </form>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}
